/**
 * EditCosyflowModal - Modal for editing cosyflow settings
 * Based on the original EditTemplateModal from templates app
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../api-helper';

// Available categories (matching the original templates app)
const CATEGORIES = [
  'General',
  'Portrait',
  'Landscape',
  'Abstract',
  'Anime',
  'Realistic',
  'Fantasy',
  'Sci-Fi',
  'Art',
  'Photography',
  'Illustration',
  'AI Music/Audio',
  'Video',
  '3D',
];

// Use cases (matching the main templates page - all 24 use cases)
const USE_CASES = [
  '3D',
  'API',
  'Anime',
  'Audio',
  'Audio Editing',
  'Audio to Audio',
  'ControlNet',
  'FLF2V',
  'Image',
  'Image Edit',
  'Image to 3D',
  'Image to Image',
  'Image to Video',
  'Inpainting',
  'LLM',
  'Outpainting',
  'Text to Audio',
  'Text to Image',
  'Text to Model',
  'Text to Video',
  'Upscale',
  'Vector',
  'Video',
  'Video to Video',
];

// Close Icon
const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// Search Icon
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Chevron Down Icon
const ChevronDownIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

// Check Icon
const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// Save Icon
const SaveIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

// Plus Icon
const PlusIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

// Image Icon
const ImageIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

// Spinner for loading state
const ButtonSpinner = () => (
  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

// Small spinner for image upload
const SmallSpinner = () => (
  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
);

// Toggle switch component
const Toggle = ({ checked, onChange, disabled, label, description }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <div className="relative">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
        disabled={disabled}
      />
      <div className={`w-12 h-7 rounded-full transition-colors ${checked ? 'bg-yellow' : 'bg-white/20'}`}>
        <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
      </div>
    </div>
    <div>
      <span className="text-lg text-white">{label}</span>
      {description && <p className="text-base text-white/50">{description}</p>}
    </div>
  </label>
);

// Searchable Use Case Select component
const SearchableUseCaseSelect = ({ value, onChange, useCases, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter use cases based on search
  const filteredUseCases = useMemo(() => {
    if (!searchQuery) return useCases;
    const query = searchQuery.toLowerCase();
    return useCases.filter(uc => uc.toLowerCase().includes(query));
  }, [useCases, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (useCase) => {
    onChange(useCase);
    setIsOpen(false);
    setSearchQuery('');
  };

  const displayValue = value || 'Select a use case...';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 bg-dark-blue/30 border border-white/20 rounded-lg px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-cyan/50 cursor-pointer hover:border-white/40 transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : ''
        } ${value ? 'text-white' : 'text-white/40'}`}
        disabled={disabled}
      >
        <span className="truncate">{displayValue}</span>
        <ChevronDownIcon />
      </button>

      {/* Dropdown Panel */}
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 mt-1 w-full bg-dark-blue border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-white/10">
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                <SearchIcon />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search use cases..."
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white text-lg placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-cyan/50"
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-64 overflow-y-auto">
            {filteredUseCases.length === 0 ? (
              <div className="px-4 py-3 text-white/40 text-base">No matching use cases</div>
            ) : (
              filteredUseCases.map((uc) => (
                <button
                  key={uc}
                  type="button"
                  onClick={() => handleSelect(uc)}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left text-lg transition-colors ${
                    value === uc
                      ? 'bg-yellow/20 text-yellow'
                      : 'text-white hover:bg-white/10'
                  }`}
                >
                  <span>{uc}</span>
                  {value === uc && <CheckIcon />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export function EditCosyflowModal({
  cosyflow,
  isOpen,
  onClose,
  onSave,
  updateCosyflow,
  accountNo,
  generateContent,
  fetchModelDefaults,
}) {
  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [useCase, setUseCase] = useState('');
  const [basePrompt, setBasePrompt] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [distributed, setDistributed] = useState(false);

  // Example images state
  const [exampleImages, setExampleImages] = useState([]); // Array of { url, isNew, file?, uploading? }
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Generation state
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [modelDefaults, setModelDefaults] = useState(null);
  const [isLoadingModel, setIsLoadingModel] = useState(false);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Parse example images from cosyflow
  const parseExampleImages = useCallback((cosyflow) => {
    if (!cosyflow) return [];

    // Check for example_images array
    if (cosyflow.example_images && Array.isArray(cosyflow.example_images)) {
      return cosyflow.example_images.map((url, index) => ({
        id: `existing-${index}`,
        url: url,
        isNew: false,
      }));
    }

    // Check for images in gallery_images or other fields
    if (cosyflow.gallery_images && Array.isArray(cosyflow.gallery_images)) {
      return cosyflow.gallery_images.map((url, index) => ({
        id: `gallery-${index}`,
        url: url,
        isNew: false,
      }));
    }

    // Check for imageUrl (single image)
    if (cosyflow.imageUrl) {
      return [{
        id: 'main-image',
        url: cosyflow.imageUrl,
        isNew: false,
      }];
    }

    return [];
  }, []);

  // Load model defaults for the cosyflow
  const loadModelDefaults = useCallback(async (modelName) => {
    if (!modelName || !fetchModelDefaults) return;
    setIsLoadingModel(true);
    try {
      const defaults = await fetchModelDefaults(modelName);
      setModelDefaults(defaults);
    } catch (error) {
      console.error('Error loading model defaults:', error);
      setModelDefaults(null);
    } finally {
      setIsLoadingModel(false);
    }
  }, [fetchModelDefaults]);

  // Reset form when cosyflow changes
  useEffect(() => {
    if (cosyflow) {
      setTitle(cosyflow.title || '');
      setDescription(cosyflow.description || '');
      setCategory(cosyflow.category || 'General');
      setUseCase(cosyflow.useCase || cosyflow.use_case || '');
      setBasePrompt(cosyflow.basePrompt || cosyflow.base_prompt || '');
      setIsPublic(cosyflow.isPublic !== false && cosyflow.public !== false);
      setDistributed(cosyflow.distributed || false);
      setExampleImages(parseExampleImages(cosyflow));
      setGenerationPrompt('');
      setModelDefaults(null);
      setError(null);
      setSuccess(false);

      // Load model defaults for generation
      const modelName = cosyflow.title || cosyflow.model || cosyflow.name;
      if (modelName) {
        loadModelDefaults(modelName);
      }
    }
  }, [cosyflow, parseExampleImages, loadModelDefaults]);

  // Handle content generation
  const handleGenerateContent = useCallback(async () => {
    if (!generationPrompt.trim()) {
      setError('Please enter a prompt to generate content.');
      return;
    }

    if (!accountNo) {
      setError('You must be logged in to generate content.');
      return;
    }

    if (!generateContent) {
      setError('Generation is not available.');
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(0);
    setError(null);

    try {
      const modelName = cosyflow?.title || cosyflow?.model || cosyflow?.name;
      const result = await generateContent({
        prompt: generationPrompt,
        basePrompt: basePrompt,
        model: modelName,
        modelDefaults,
        imageInputs: {},
        onProgress: setGenerationProgress,
      });

      if (result && result.image) {
        // Add the generated image to example images
        const newImage = {
          id: `generated-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          url: result.image.startsWith('data:') ? result.image : `data:image/webp;base64,${result.image}`,
          serverUrl: result.serverUrl,
          isNew: true,
          uploading: false,
        };

        setExampleImages(prev => [...prev, newImage]);
        setGenerationPrompt(''); // Clear prompt after successful generation
      }
    } catch (err) {
      console.error('Generation error:', err);
      setError(err.message || 'Failed to generate content.');
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  }, [generationPrompt, accountNo, generateContent, cosyflow, basePrompt, modelDefaults]);

  // Upload a single image to the server
  const uploadImage = useCallback(async (file) => {
    try {
      const apiBase = await getApiBaseUrl();
      const formData = new FormData();
      formData.append('image', file);
      formData.append('account_no', accountNo);

      const response = await axios.post(`${apiBase}/api/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
        },
      });

      if (response.data?.url) {
        return response.data.url;
      } else if (response.data?.image_url) {
        return response.data.image_url;
      }
      throw new Error('No URL in response');
    } catch (err) {
      console.error('Error uploading image:', err);
      throw err;
    }
  }, [accountNo]);

  // Handle file selection
  const handleFileSelect = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const newImages = [];
    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;

      const id = `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const previewUrl = URL.createObjectURL(file);

      newImages.push({
        id,
        url: previewUrl,
        isNew: true,
        file,
        uploading: true,
      });
    }

    // Add images to state with uploading status
    setExampleImages(prev => [...prev, ...newImages]);

    // Upload each image
    for (const img of newImages) {
      try {
        const uploadedUrl = await uploadImage(img.file);
        setExampleImages(prev =>
          prev.map(item =>
            item.id === img.id
              ? { ...item, url: uploadedUrl, uploading: false }
              : item
          )
        );
        // Revoke object URL after upload
        URL.revokeObjectURL(img.url);
      } catch (err) {
        // Remove failed upload
        setExampleImages(prev => prev.filter(item => item.id !== img.id));
        URL.revokeObjectURL(img.url);
        setError(`Failed to upload image: ${err.message}`);
      }
    }
  }, [uploadImage]);

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  // Remove an image
  const handleRemoveImage = useCallback((imageId) => {
    setExampleImages(prev => {
      const image = prev.find(img => img.id === imageId);
      if (image?.isNew && image.url.startsWith('blob:')) {
        URL.revokeObjectURL(image.url);
      }
      return prev.filter(img => img.id !== imageId);
    });
  }, []);

  const handleSave = async () => {
    if (!accountNo) {
      setError('You must be logged in to edit cosyflows.');
      return;
    }

    if (!title.trim()) {
      setError('Title is required.');
      return;
    }

    // Check if any images are still uploading
    const stillUploading = exampleImages.some(img => img.uploading);
    if (stillUploading) {
      setError('Please wait for all images to finish uploading.');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Get image URLs (prefer serverUrl over base64, filter out blob URLs)
      const imageUrls = exampleImages
        .filter(img => !img.uploading && !img.url.startsWith('blob:'))
        .map(img => img.serverUrl || img.url);

      const response = await updateCosyflow({
        accountNo,
        uid: cosyflow.uid || cosyflow.id,
        title: title.trim(),
        description: description.trim(),
        category,
        useCase,
        basePrompt: basePrompt.trim() || undefined,
        isPublic,
        distributed,
        exampleImages: imageUrls,
      });

      if (response?.error) {
        setError(response.error);
        return;
      }

      setSuccess(true);

      // Notify parent of the update
      onSave?.({
        title: title.trim(),
        description: description.trim(),
        category,
        useCase,
        basePrompt: basePrompt.trim() || undefined,
        isPublic,
        distributed,
        exampleImages: imageUrls,
      });

      // Close modal after short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error updating cosyflow:', err);
      setError(err.message || 'Failed to update cosyflow.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-dark-blue/90 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto mx-4 bg-dark-blue/95 border border-white/20 rounded-2xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-dark-blue border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-3xl font-bold text-white">Edit Template</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/60 hover:text-white"
            disabled={isSaving}
          >
            <CloseIcon />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Error/Success Messages */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-base">
              {error}
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-base">
              Cosyflow updated successfully!
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-lg font-medium text-white mb-2">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter cosyflow title"
              className="w-full px-4 py-3 bg-dark-blue/30 border border-white/20 rounded-lg text-white text-lg placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 focus:border-cyan"
              disabled={isSaving}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-lg font-medium text-white mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this cosyflow does..."
              className="w-full h-28 px-4 py-3 bg-dark-blue/30 border border-white/20 rounded-lg text-white text-lg placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 focus:border-cyan resize-none"
              disabled={isSaving}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-lg font-medium text-white mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-3 bg-dark-blue/30 border border-white/20 rounded-lg text-white text-lg focus:outline-none focus:ring-2 focus:ring-cyan/50 focus:border-cyan"
              disabled={isSaving}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat} className="bg-dark-blue">
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Use Case */}
          <div>
            <label className="block text-lg font-medium text-white mb-2">
              Use Case
            </label>
            <SearchableUseCaseSelect
              value={useCase}
              onChange={setUseCase}
              useCases={USE_CASES}
              disabled={isSaving}
            />
          </div>

          {/* Base Prompt */}
          <div>
            <label className="block text-lg font-medium text-white mb-2">
              Base Prompt
            </label>
            <textarea
              value={basePrompt}
              onChange={(e) => setBasePrompt(e.target.value)}
              placeholder="Enter the base prompt that will be combined with user prompts..."
              className="w-full h-36 px-4 py-3 bg-dark-blue/30 border border-white/20 rounded-lg text-white text-lg placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 focus:border-cyan resize-none"
              disabled={isSaving}
            />
            <p className="text-base text-white/50 mt-1">
              This prompt will be prepended to user prompts when generating.
            </p>
          </div>

          {/* Example Images */}
          <div>
            <label className="block text-lg font-medium text-white mb-2">
              Example Images
            </label>
            <p className="text-base text-white/50 mb-3">
              Generate new images or upload existing ones to showcase what this cosyflow can produce.
            </p>

            {/* Generation Section */}
            {generateContent && (
              <div className="border border-white/20 rounded-xl p-4 mb-4 bg-dark-blue/30">
                <label className="block text-lg font-medium text-white mb-2">
                  Generate New Examples
                </label>
                <div className="space-y-3">
                  <textarea
                    value={generationPrompt}
                    onChange={(e) => setGenerationPrompt(e.target.value)}
                    placeholder="Enter a prompt to generate example images..."
                    rows={2}
                    className="w-full px-4 py-3 bg-dark-blue/50 border border-white/20 rounded-lg text-white text-lg placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-cyan/50 resize-none"
                    disabled={isGenerating || isSaving}
                  />
                  <button
                    onClick={handleGenerateContent}
                    className="w-full px-4 py-3 rounded-lg font-medium text-lg bg-yellow hover:bg-yellow/90 text-dark-blue disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                    disabled={isGenerating || isSaving || !generationPrompt.trim() || isLoadingModel}
                  >
                    {isGenerating ? (
                      <>
                        <SmallSpinner />
                        Generating... {generationProgress > 0 ? `${generationProgress}%` : ''}
                      </>
                    ) : isLoadingModel ? (
                      <>
                        <SmallSpinner />
                        Loading model...
                      </>
                    ) : (
                      <>
                        <PlusIcon />
                        Generate
                      </>
                    )}
                  </button>
                </div>
                <p className="text-base text-white/40 mt-2">
                  Generated images will be added to the gallery below.
                </p>
              </div>
            )}

            {/* Current Images Grid */}
            {exampleImages.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mb-4">
                {exampleImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="relative aspect-square rounded-lg overflow-hidden border border-white/20 group"
                  >
                    <img
                      src={image.url}
                      alt={`Example ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    {/* Uploading Overlay */}
                    {image.uploading && (
                      <div className="absolute inset-0 bg-dark-blue/80 flex items-center justify-center">
                        <SmallSpinner />
                      </div>
                    )}
                    {/* Remove Button */}
                    {!image.uploading && (
                      <button
                        onClick={() => handleRemoveImage(image.id)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500/80 hover:bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        disabled={isSaving}
                      >
                        <CloseIcon />
                      </button>
                    )}
                    {/* Image Number */}
                    <div className="absolute bottom-1 left-1 w-6 h-6 bg-black/60 text-white text-sm rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Drop Zone / Upload Button */}
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`
                border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
                ${isDragging
                  ? 'border-cyan bg-cyan/10'
                  : 'border-white/20 hover:border-cyan/50 hover:bg-dark-blue/20'
                }
                ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              <div className="text-white/40 mb-2 flex justify-center">
                <ImageIcon />
              </div>
              <p className="text-lg text-white/60 mb-1">
                {isDragging ? 'Drop images here' : 'Click or drag images to upload'}
              </p>
              <p className="text-base text-white/40">
                PNG, JPG, WEBP up to 10MB each
              </p>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileSelect(e.target.files)}
              className="hidden"
              disabled={isSaving}
            />
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white">Settings</h3>

            <div className="space-y-3">
              {/* Public Toggle */}
              <Toggle
                checked={isPublic}
                onChange={setIsPublic}
                disabled={isSaving}
                label="Public"
                description="Make this cosyflow visible to everyone"
              />

              {/* Distributed Toggle */}
              <Toggle
                checked={distributed}
                onChange={setDistributed}
                disabled={isSaving}
                label="Distributed"
                description="Enable distribution across multiple workers"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-dark-blue border-t border-white/10 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-3 bg-white/10 hover:bg-white/20 text-white text-lg font-medium rounded-lg transition-colors"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-5 py-3 bg-yellow hover:bg-yellow/90 text-dark-blue text-lg font-medium rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving || !title.trim()}
          >
            {isSaving ? (
              <>
                <ButtonSpinner />
                Saving...
              </>
            ) : (
              <>
                <SaveIcon />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default EditCosyflowModal;
